import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';

const PermissionItem = ({ 
  title, 
  expanded, 
  onToggle, 
  role,
  permissions,
  onPermissionChange,
  sectionKey,
  children
}) => {
  // Check if all child permissions are checked
  const areAllChildrenChecked = () => {
    const sectionPermissions = permissions[sectionKey];
    if (!sectionPermissions) return false;
    
    // Get all permission keys except 'enabled'
    const childPermissionKeys = Object.keys(sectionPermissions).filter(key => key !== 'enabled');
    
    // Check if all child permissions are true
    return childPermissionKeys.length > 0 && 
           childPermissionKeys.every(key => sectionPermissions[key] === true);
  };

  // Handle parent checkbox change
  const handleParentChange = (checked) => {
    const sectionPermissions = permissions[sectionKey];
    if (!sectionPermissions) return;

    // Get all permission keys except 'enabled'
    const childPermissionKeys = Object.keys(sectionPermissions).filter(key => key !== 'enabled');
    
    // Update all child permissions to match parent
    childPermissionKeys.forEach(key => {
      onPermissionChange(sectionKey, key, checked);
    });
    
    // Update parent enabled state
    onPermissionChange(sectionKey, 'enabled', checked);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton 
          size="small" 
          onClick={onToggle}
          sx={{ 
            p: 0.5,
            mr: 0.5,
            '&:hover': { backgroundColor: 'transparent' }
          }}
        >
          {expanded ? 
            <RemoveIcon fontSize="small" /> : 
            <AddIcon fontSize="small" />
          }
        </IconButton>
        <FormControlLabel
          control={
            <Checkbox 
              size="small"
              checked={areAllChildrenChecked()}
              onChange={(e) => handleParentChange(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {title}
            </Typography>
          }
        />
      </Box>
      {expanded && (
        <Box sx={{ ml: 4, mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {children}
        </Box>
      )}
    </Box>
  );
};

const PermissionsSection = ({ role, expandedSections, toggleSection }) => {
  const [permissions, setPermissions] = useState({
    sales: {
      enabled: false,
      view: true,
      insert: false,
      paymentStatus: false
    },
    purchase: {
      enabled: false,
      view: true,
      insert: false
    },
    creditNote: {
      enabled: false,
      view: true,
      insert: false
    },
    debitNote: {
      enabled: false,
      view: true,
      insert: false
    },
    reports: {
      enabled: false,
      view: false
    }
  });

  const handlePermissionChange = (section, permission, value) => {
    setPermissions(prev => {
      const newPermissions = {
        ...prev,
        [section]: {
          ...prev[section],
          [permission]: value
        }
      };

      // If this is a child permission change, check if we need to update parent
      if (permission !== 'enabled') {
        const sectionPermissions = newPermissions[section];
        const childPermissionKeys = Object.keys(sectionPermissions).filter(key => key !== 'enabled');
        const allChildrenChecked = childPermissionKeys.every(key => sectionPermissions[key] === true);
        
        // Update parent enabled state based on children
        newPermissions[section].enabled = allChildrenChecked;
      }

      return newPermissions;
    });
  };

  return (
    <Box sx={{ 
      border: '1px solid',
      borderColor: 'divider',
      p: 2
    }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
        Permissions
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <PermissionItem 
          title="1. Sales"
          expanded={expandedSections.sales}
          onToggle={() => toggleSection('sales')}
          role={role}
          permissions={permissions}
          onPermissionChange={handlePermissionChange}
          sectionKey="sales"
        >
          <FormControlLabel
            control={
              <Checkbox 
                size="small"
                checked={permissions.sales.view}
                onChange={(e) => handlePermissionChange('sales', 'view', e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                1.1 View
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox 
                size="small"
                checked={permissions.sales.insert}
                onChange={(e) => handlePermissionChange('sales', 'insert', e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                1.2 Insert
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox 
                size="small"
                checked={permissions.sales.paymentStatus}
                onChange={(e) => handlePermissionChange('sales', 'paymentStatus', e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                1.3 Payment Status
              </Typography>
            }
          />
        </PermissionItem>

        <PermissionItem 
          title="2. Purchase"
          expanded={expandedSections.purchase}
          onToggle={() => toggleSection('purchase')}
          role={role}
          permissions={permissions}
          onPermissionChange={handlePermissionChange}
          sectionKey="purchase"
        >
          <FormControlLabel
            control={
              <Checkbox 
                size="small"
                checked={permissions.purchase.view}
                onChange={(e) => handlePermissionChange('purchase', 'view', e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                2.1 View
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox 
                size="small"
                checked={permissions.purchase.insert}
                onChange={(e) => handlePermissionChange('purchase', 'insert', e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                2.2 Insert
              </Typography>
            }
          />
        </PermissionItem>

        <PermissionItem 
          title="3. Credit Note"
          expanded={expandedSections.creditNote}
          onToggle={() => toggleSection('creditNote')}
          role={role}
          permissions={permissions}
          onPermissionChange={handlePermissionChange}
          sectionKey="creditNote"
        >
          <FormControlLabel
            control={
              <Checkbox 
                size="small"
                checked={permissions.creditNote.view}
                onChange={(e) => handlePermissionChange('creditNote', 'view', e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                3.1 View
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox 
                size="small"
                checked={permissions.creditNote.insert}
                onChange={(e) => handlePermissionChange('creditNote', 'insert', e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                3.2 Insert
              </Typography>
            }
          />
        </PermissionItem>

        <PermissionItem 
          title="4. Debit Note"
          expanded={expandedSections.debitNote}
          onToggle={() => toggleSection('debitNote')}
          role={role}
          permissions={permissions}
          onPermissionChange={handlePermissionChange}
          sectionKey="debitNote"
        >
          <FormControlLabel
            control={
              <Checkbox 
                size="small"
                checked={permissions.debitNote.view}
                onChange={(e) => handlePermissionChange('debitNote', 'view', e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                4.1 View
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox 
                size="small"
                checked={permissions.debitNote.insert}
                onChange={(e) => handlePermissionChange('debitNote', 'insert', e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                4.2 Insert
              </Typography>
            }
          />
        </PermissionItem>

        <PermissionItem 
          title="5. Reports"
          expanded={expandedSections.reports}
          onToggle={() => toggleSection('reports')}
          role={role}
          permissions={permissions}
          onPermissionChange={handlePermissionChange}
          sectionKey="reports"
        >
          <FormControlLabel
            control={
              <Checkbox 
                size="small"
                checked={permissions.reports.view}
                onChange={(e) => handlePermissionChange('reports', 'view', e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                5.1 View
              </Typography>
            }
          />
        </PermissionItem>
      </Box>
    </Box>
  );
};

export default PermissionsSection; 